import React, { FC, useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Page } from 'src/components';
import { PremiumConfigProps } from 'src/hooks/usePremiumConfig';
import useSafePush from 'src/hooks/useSafePush';
import { makeUrlRelativeOrDashboard } from 'src/utils/location';
import { ROUTES } from 'src/constants/routes.const';
import { UpsellPage } from './UpsellPage';

const msgs = defineMessages({
  pageTitle: {
    id: 'ea_plans_seo_title',
    defaultMessage: 'Memrise Language Learning App - Subscription Plans',
  },
  pageDescription: {
    id: 'ea_plans_seo_description',
    defaultMessage:
      'Learning a language with Memrise is easy and affordable. Browse our Pro Plan options and get the best price to start learning. Sign up for free to start.',
  },
});

const Plans: FC = () => {
  const intl = useIntl();
  const { query } = useRouter();

  // Prefer 'offer' as the query parameter name, but support 'coupon' for redirects from legacy URLs
  const queryOffer = query.offer || query.coupon;
  const specialOffer = typeof queryOffer === 'string' ? queryOffer : undefined;

  // Special pricing can't be shown on the payment plans page.
  // `skipPlans` should be used to directly navigate users to the checkout page.
  const specialPrice =
    String(query.skipPlans) === 'true' && typeof query.specialPrice === 'string'
      ? query.specialPrice
      : undefined;

  const premiumConfigProps = useMemo((): PremiumConfigProps => {
    const modifiers = (query.mode ? String(query.mode) : '').split(',');

    return {
      specialPrice,
      specialOffer: specialOffer
        ? {
            promoCode: specialOffer,
            priority: modifiers.includes('high') ? 'high' : undefined,
            isRequired: modifiers.includes('required'),
          }
        : null,
    };
  }, [specialPrice, specialOffer, query.mode]);

  const subscribeHandlerConfig = useMemo(
    () => ({ autoSubscribeToSinglePrice: !!specialPrice }),
    [specialPrice],
  );

  const { safePush } = useSafePush();
  const handleClose = useCallback(() => {
    const nextPage =
      typeof query.next === 'string' ? makeUrlRelativeOrDashboard(query.next) : ROUTES.dashboard;
    void safePush(nextPage);
  }, [query, safePush]);

  return (
    <Page footerStyle="none">
      <Head>
        <title>{intl.formatMessage(msgs.pageTitle)}</title>
        <meta name="description" content={intl.formatMessage(msgs.pageDescription)} />
        <link rel="canonical" href="https://app.memrise.com/payment/plans" id="canonical" />
      </Head>
      <UpsellPage
        premiumConfigProps={premiumConfigProps}
        subscribeHandlerConfig={subscribeHandlerConfig}
        onClose={handleClose}
      />
    </Page>
  );
};

export default Plans;
